// mds-dojo — dijit/form/_ListBase — FAITHFUL port of references/dijit/form/_ListBase.js.
// Codemod pipeline; declare from the faithful runtime. Adaptations:
//  - `on.selector(...)`: mds dojo/on has no .selector, so a faithful local delegation shim
//    (onSelector) is defined below. It reproduces dojo/on.selector's function-selector path:
//    walk up from event.target to the bound node calling the selector fn, and on a match invoke
//    the listener with `this` = the matched node. Used exactly as real dojo (via on(node, ext, cb),
//    which mds on supports because it dispatches function-type "extension events").
import declare from '../../dojo/_base/declare-runtime'; // declare
import on from '../../dojo/on';
import winUtils from '../../dojo/window'; // winUtils.scrollIntoView

// Faithful local shim for dojo/on.selector (function-selector form only, which is all _ListBase uses).
function onSelector(selector: any, eventType: any) {
	return function (target: any, listener: any) {
		function select(eventTarget: any): any {
			// Walk up the DOM from the event target to the bound node, testing the selector fn.
			while (!selector(eventTarget, selector, target)) {
				if (eventTarget == target || !(eventTarget = eventTarget.parentNode) || eventTarget.nodeType != 1) {
					return null;
				}
			}
			return eventTarget;
		}
		return on(target, eventType, function (event: any) {
			var eventTarget = event.target;
			var selectorTarget = select(eventTarget);
			if (selectorTarget) {
				return listener.call(selectorTarget, event);
			}
		});
	};
}

export default declare("dijit.form._ListBase", null, {
	// summary:
	//		Focus-less menu to handle UI events consistently.
	//		Abstract methods that must be defined externally:
	//
	//		- onSelect: item is active (mousedown but not yet mouseup, or keyboard arrow selected but no Enter)
	//		- onDeselect:  cancels onSelect
	// tags:
	//		private

	// selected: DOMNode
	//		currently selected node
	selected: null,

	_listConnect: function (/*String|Function*/ eventType: any, /*String*/ callbackFuncName: any) {
		// summary:
		//		Connects 'containerNode' to specified method of this object
		//		and automatically registers for 'disconnect' on widget destroy.
		// tags:
		//		private

		var self = this;
		return self.own(on(self.containerNode,
			onSelector(
				function (eventTarget: any, selector: any, target: any) {
					return eventTarget.parentNode == target;
				},
				eventType
			),
			function (this: any, evt: any) {
				self[callbackFuncName](evt, this);
			}
		));
	},

	selectFirstNode: function () {
		// summary:
		//		Select the first displayed item in the list.
		var first = this.containerNode.firstChild;
		while (first && first.style.display == "none") {
			first = first.nextSibling;
		}
		this._setSelectedAttr(first, true);
	},

	selectLastNode: function () {
		// summary:
		//		Select the last displayed item in the list
		var last = this.containerNode.lastChild;
		while (last && last.style.display == "none") {
			last = last.previousSibling;
		}
		this._setSelectedAttr(last, true);
	},

	selectNextNode: function () {
		// summary:
		//		Select the item just below the current selection.
		//		If nothing selected, select first node.
		var selectedNode = this.selected;
		if (!selectedNode) {
			this.selectFirstNode();
		} else {
			var next = selectedNode.nextSibling;
			while (next && next.style.display == "none") {
				next = next.nextSibling;
			}
			if (!next) {
				this.selectFirstNode();
			} else {
				this._setSelectedAttr(next, true);
			}
		}
	},

	selectPreviousNode: function () {
		// summary:
		//		Select the item just above the current selection.
		//		If nothing selected, select last node (if
		//		you select Previous and try to keep scrolling up the list).
		var selectedNode = this.selected;
		if (!selectedNode) {
			this.selectLastNode();
		} else {
			var prev = selectedNode.previousSibling;
			while (prev && prev.style.display == "none") {
				prev = prev.previousSibling;
			}
			if (!prev) {
				this.selectLastNode();
			} else {
				this._setSelectedAttr(prev, true);
			}
		}
	},

	_setSelectedAttr: function (/*DomNode*/ node: any, /*Boolean*/ scroll: any) {
		// summary:
		//		Does the actual select.
		// node:
		//		The option to select
		// scroll:
		//		If necessary, scroll node into view.  Set to false for mouse/touch to
		//		avoid jumping problems on mobile/RTL, see https://bugs.dojotoolkit.org/ticket/17739.
		if (this.selected != node) {
			var selectedNode = this.selected;
			if (selectedNode) {
				this.onDeselect(selectedNode);
			}
			if (node) {
				if (scroll) {
					winUtils.scrollIntoView(node);
				}
				this.onSelect(node);
			}
			this._set("selected", node);
		} else if (node) {
			this.onSelect(node);
		}
	}
});
